import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './chartSetup';
import { apiFetch, fetchAllPages } from './api';
import { recordQuery, getRecentQueries } from './queryHistory';
import { getRiskLevel } from './utils/risk';
import Sidebar from './components/Sidebar';
import MobileNav from './components/MobileNav';
import Header from './components/Header';
import DashboardView from './components/DashboardView';
import IntelligenceView from './components/IntelligenceView';
import AnalyticsView from './components/AnalyticsView';
import RunsView from './components/RunsView';
import AiPage from './components/AiPage';
import AiBotWidget from './components/AiBotWidget';
import BotIntroOverlay from './components/BotIntroOverlay';
import CompaniesView from './components/CompaniesView';
import BriefsView from './components/BriefsView';
import SettingsView from './components/SettingsView';
import AddCompanyModal from './components/AddCompanyModal';
import { ConfirmCompanyModal, CompanyReadyModal } from './components/CompanyActionModal';
import AlertDetailPage from './components/AlertDetailPage';
import { getEmailActivity, getScheduledReports } from './utils/emailActivity';
import {
  saveWatchlistSnapshot,
  removeWatchlistSnapshot,
  acknowledgeWatchlistItem,
  ensureWatchlistSnapshot,
  countUpdatedWatchlistItems,
  getUpdatedWatchlistIds,
} from './utils/watchlistSnapshots';
import { dateFilterToApiParams, getDateFilterLabel, matchesDateFilter, recordKey } from './utils/format';
import { areSameStory } from './utils/storyGrouping';
import {
  buildNotificationFeed,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  notifyBriefSent,
  notifyCompanyAdded,
  notifyCompanyResultsReady,
} from './utils/notifications';
import {
  createChatSession,
  deriveChatTitle,
  loadChatSessions,
  openOrCreateBlankSession,
  resolveChatTitle,
  saveChatSessions,
} from './utils/chatSessions';

const AI_MODEL_STORAGE_KEY = 'corpintel_ai_model';

function loadStoredAiModel() {
  try {
    return localStorage.getItem(AI_MODEL_STORAGE_KEY) || '';
  } catch (err) {
    return '';
  }
}

function App() {
  // ─── State ─────────────────────────────────────────────────────────────
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Intelligence page filters — applied client-side over already-fetched records
  const [activeCompanies, setActiveCompanies] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState({ preset: 'all', customDate: '' });
  const [sortOrder, setSortOrder] = useState('newest');



  const [companies, setCompanies] = useState([]);
  const [companyStats, setCompanyStats] = useState(null);
  const [runLogs, setRunLogs] = useState([]);
  
  // Watchlist
  const [watchlist, setWatchlist] = useState(() => {
    try {
      const saved = localStorage.getItem('corpintel_watchlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Navigation view
  const [view, setView] = useState('dashboard'); // dashboard | intelligence | analytics | runs | ai | settings
  const [showBotIntro, setShowBotIntro] = useState(false);

  const handleBotIntroDone = useCallback(() => setShowBotIntro(false), []);

  // Navigate to AI with zoom intro + land on a blank New chat (not the last open
  // convo). Pass keepChat when the caller has already picked which chat to open
  // - opening a specific conversation from another view (sidebar, or a "results
  // ready" notification) used to get overridden by the blank chat right after.
  const navigateToView = useCallback((nextView, options = {}) => {
    if (nextView === 'ai' && view !== 'ai') {
      if (!options.keepChat) {
        setChatSessions(sessions => {
          const next = openOrCreateBlankSession(sessions, null);
          setActiveChatId(next.activeChatId);
          return next.sessions;
        });
      }
      setAiPageInput('');
      setView('ai');
      return;
    }
    setView(nextView);
  }, [view]);
  const [settingsInitialMode, setSettingsInitialMode] = useState(null);

  const [emailActivity, setEmailActivity] = useState([]);
  const [scheduledReports, setScheduledReports] = useState([]);

  const [showAddCompany, setShowAddCompany] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);
  const [addCompanyError, setAddCompanyError] = useState('');
  // "add Tesla" typed into the chat: the proposal awaiting confirmation, the
  // sweep being watched after confirming, and the results-ready popup.
  const [chatAction, setChatAction] = useState(null);
  const [chatActionBusy, setChatActionBusy] = useState(false);
  const [chatActionError, setChatActionError] = useState('');
  const [watchedFetch, setWatchedFetch] = useState(null);
  const [companyReady, setCompanyReady] = useState(null);
  const [notificationTick, setNotificationTick] = useState(0);
  const [watchlistRevision, setWatchlistRevision] = useState(0);
  const emailPulseCount = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return emailActivity.filter(a => new Date(a.createdAt).getTime() >= cutoff).length;
  }, [emailActivity]);

  // AI: per-article summarize
  const [summarizingId, setSummarizingId] = useState(null);

  // AI popup ("what can I ask" overview + suggestions, opened via header icon)
  const [showAiPopup, setShowAiPopup] = useState(false);
  const [aiOverview, setAiOverview] = useState(null); // { overview, suggestions, total_records }
  const [aiOverviewLoading, setAiOverviewLoading] = useState(false);
  const [popupQuestion, setPopupQuestion] = useState('');
  const [popupAnswer, setPopupAnswer] = useState(null); // { answer, sources }
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupError, setPopupError] = useState(null);

  // Dedicated AI page - multiple named chats persisted in localStorage, one active at a time
  const [chatSessions, setChatSessions] = useState(() => {
    const stored = loadChatSessions();
    return stored.length > 0 ? stored : [createChatSession()];
  });
  const [activeChatId, setActiveChatId] = useState(() => chatSessions[0]?.id);
  const [aiPageInput, setAiPageInput] = useState('');
  const conversation = useMemo(
    () => chatSessions.find(s => s.id === activeChatId)?.turns || [],
    [chatSessions, activeChatId],
  );

  // AI model selection - persisted across sessions, defaults to whatever the
  // backend reports as its confirmed-working default (see /ai/models).
  const [aiModels, setAiModels] = useState([]);
  const [defaultAiModel, setDefaultAiModel] = useState('');
  const [selectedAiModel, setSelectedAiModel] = useState(loadStoredAiModel);

  // ─── API Calls ──────────────────────────────────────────────────────────
  // Fetches every page from the backend (capped at 100/page) so dashboard stats,
  // charts, and Intelligence filtering all see the full record set, not just page 1.
  const fetchIntelligence = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = searchQuery ? '/search' : '/intelligence';
      const dateParams = dateFilterToApiParams(dateFilter);
      const baseParams = searchQuery ? { q: searchQuery, ...dateParams } : dateParams;
      const { results, total } = await fetchAllPages(endpoint, baseParams);
      setRecords(results);
      setTotalRecords(total);
    } catch (err) {
      setError(err.message);
      setRecords([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, dateFilter]);

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await apiFetch('/companies');
      setCompanies((data || []).map(c => ({ name: c.name || c })));
    } catch (err) {
      console.error('Failed to fetch companies', err);
    }
  }, []);

  const fetchCompanyStats = useCallback(async (companyName) => {
    if (!companyName) { setCompanyStats(null); return; }
    try {
      const data = await apiFetch(`/companies/${encodeURIComponent(companyName)}/stats`);
      setCompanyStats(data);
    } catch (err) {
      console.error('Failed to fetch company stats', err);
      setCompanyStats(null);
    }
  }, []);

  const fetchRunLogs = useCallback(async () => {
    try {
      const data = await apiFetch('/runs');
      setRunLogs(data || []);
    } catch (err) {
      console.error('Failed to fetch run logs', err);
      setRunLogs([]);
    }
  }, []);

  useEffect(() => {
    fetchIntelligence();
  }, [fetchIntelligence]);

  useEffect(() => {
    fetchRunLogs();
  }, [fetchRunLogs]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [activity, reports] = await Promise.all([
          getEmailActivity(),
          getScheduledReports(),
        ]);
        if (!cancelled) {
          setEmailActivity(activity);
          setScheduledReports(reports);
        }
      } catch (err) {
        console.error('Failed to load email settings from DB', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Company stats mirror the Intelligence page's active company chip when exactly one is selected
  useEffect(() => {
    if (activeCompanies.length === 1) fetchCompanyStats(activeCompanies[0]);
    else setCompanyStats(null);
  }, [activeCompanies, fetchCompanyStats]);

  useEffect(() => {
    saveChatSessions(chatSessions);
  }, [chatSessions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch('/ai/models');
        if (cancelled) return;
        setAiModels(data.models || []);
        setDefaultAiModel(data.default || '');
      } catch (err) {
        console.error('Failed to load AI models', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try {
      if (selectedAiModel) localStorage.setItem(AI_MODEL_STORAGE_KEY, selectedAiModel);
    } catch (err) {
      // localStorage unavailable — persistence is a nice-to-have, safe to skip
    }
  }, [selectedAiModel]);

  // Once models load, fall back to the backend's confirmed-working default
  // if nothing was previously selected, or if a stored choice is no longer offered.
  useEffect(() => {
    if (!defaultAiModel) return;
    if (!selectedAiModel || (aiModels.length > 0 && !aiModels.includes(selectedAiModel))) {
      setSelectedAiModel(defaultAiModel);
    }
  }, [defaultAiModel, aiModels, selectedAiModel]);

  // ─── Handlers ──────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) recordQuery(searchQuery);
    fetchIntelligence();
  };

  const buildAiContext = useCallback(() => {
    const dateParams = dateFilterToApiParams(dateFilter);
    const visibleRecords = records
      .filter(record => matchesDateFilter(record.published_at, dateFilter))
      .filter(record => activeCompanies.length === 0 || activeCompanies.includes(record.company))
      .filter(record => !categoryFilter || record.categories?.includes(categoryFilter))
      // Match on risk level, not the raw severity string. CRITICAL belongs to
      // the "high" bucket everywhere else (risk badges, the high-risk
      // notification, the backend's priorities), and the filter bar only offers
      // High/Medium/Low - so an exact match left CRITICAL records unreachable
      // by any filter while the counters still counted them.
      .filter(record => !severityFilter || getRiskLevel(record.severity) === getRiskLevel(severityFilter))
      .slice(0, 18);
    return {
      current_view: view,
      date_label: getDateFilterLabel(dateFilter),
      from_date: dateParams.from_date,
      to_date: dateParams.to_date,
      companies: activeCompanies,
      category: categoryFilter || null,
      severity: severityFilter || null,
      search_query: searchQuery || null,
      visible_record_ids: visibleRecords.map(record => record.id).filter(Boolean),
    };
  }, [activeCompanies, categoryFilter, dateFilter, records, searchQuery, severityFilter, view]);

  const openAiPopup = useCallback(async () => {
    setShowAiPopup(true);
    setPopupAnswer(null);
    setPopupError(null);
    if (aiOverview) return; // already loaded this session
    setAiOverviewLoading(true);
    try {
      const data = await apiFetch('/ai/overview', {
        method: 'POST',
        body: JSON.stringify({ recent_queries: getRecentQueries(8) }),
      });
      setAiOverview(data);
    } catch (err) {
      setAiOverview({ overview: `Couldn't load AI overview: ${err.message}`, suggestions: [], total_records: 0 });
    } finally {
      setAiOverviewLoading(false);
    }
  }, [aiOverview]);

  const askInPopup = useCallback(async (question) => {
    const q = (question ?? popupQuestion).trim();
    if (!q) return;
    recordQuery(q);
    setPopupLoading(true);
    setPopupError(null);
    setPopupAnswer(null);
    try {
      const data = await apiFetch('/ai/ask', {
        method: 'POST',
        body: JSON.stringify({ question: q, model: selectedAiModel || undefined, ...buildAiContext() }),
      });
      setPopupAnswer(data);
    } catch (err) {
      setPopupError(err.message);
    } finally {
      setPopupLoading(false);
    }
  }, [buildAiContext, popupQuestion, selectedAiModel]);

  // Updates turns within whichever chat session was active when the request
  // was sent (matched by chatId, not "the current active one") so a reply
  // still lands correctly even if the user switched chats while it was
  // loading. Names the chat from its first question as soon as that turn is added.
  // `label` lets a caller send a long, specific instruction to the model while
  // the chat bubble, the chat title, and the Recent list still show something
  // short and readable (see the AI page's follow-up chips).
  const askInConversation = useCallback(async (question, chatId, label, intent) => {
    const q = (question || '').trim();
    if (!q) return;
    const targetChatId = chatId || activeChatId;
    const shown = (label || '').trim() || q;
    // /ask is otherwise stateless, so a reply like "Nvidia" to the assistant's
    // own "which company?" arrived with no idea what it was answering. Send the
    // last few turns; the backend bounds their length.
    const history = (chatSessions.find(s => s.id === targetChatId)?.turns || [])
      .slice(-3)
      .flatMap(turn => [
        turn.question ? { role: 'user', content: turn.displayQuestion || turn.question } : null,
        turn.answer ? { role: 'assistant', content: turn.answer } : null,
      ])
      .filter(Boolean);
    recordQuery(shown);
    const turnId = `${Date.now()}-${Math.random()}`;
    const model = selectedAiModel;
    setChatSessions(sessions => sessions.map(s => {
      if (s.id !== targetChatId) return s;
      const isFirstTurn = !(s.turns || []).some(turn => (
        (turn?.displayQuestion || '').trim() || (turn?.question || '').trim()
      ));
      return {
        ...s,
        title: isFirstTurn ? deriveChatTitle(shown) : resolveChatTitle(s),
        turns: [...s.turns, { id: turnId, question: q, displayQuestion: shown, answer: null, sources: [], loading: true, error: null, model }],
      };
    }));
    try {
      const data = await apiFetch('/ai/ask', {
        method: 'POST',
        body: JSON.stringify({ question: q, model: model || undefined, intent: intent || undefined, history, ...buildAiContext() }),
      });
      setChatSessions(sessions => sessions.map(s => {
        if (s.id !== targetChatId) return s;
        return {
          ...s,
          title: resolveChatTitle(s),
          turns: s.turns.map(turn => turn.id === turnId ? {
            ...turn,
            answer: data.answer,
            sources: data.sources,
            contextUsed: data.context_used,
            followUps: data.follow_ups || [],
            loading: false,
          } : turn),
        };
      }));
      // The assistant only ever *proposes* a company add - confirming it is the
      // user's call, since it starts a multi-minute sweep that costs quota.
      if (data.action?.company) {
        setChatAction({ ...data.action, chatId: targetChatId });
      }
    } catch (err) {
      setChatSessions(sessions => sessions.map(s => (
        s.id === targetChatId
          ? { ...s, turns: s.turns.map(turn => turn.id === turnId ? { ...turn, loading: false, error: err.message } : turn) }
          : s
      )));
    }
  }, [buildAiContext, activeChatId, selectedAiModel, chatSessions]);

  // A status line the app writes itself - no model call, no user question.
  const appendAssistantNote = useCallback((chatId, text) => {
    setChatSessions(sessions => sessions.map(s => (
      s.id === chatId
        ? {
          ...s,
          turns: [...s.turns, {
            id: `${Date.now()}-note`,
            question: null,
            answer: text,
            sources: [],
            loading: false,
            error: null,
          }],
        }
        : s
    )));
  }, []);

  const confirmChatAction = useCallback(async () => {
    if (!chatAction) return;
    const { type, company, chatId } = chatAction;
    setChatActionBusy(true);
    setChatActionError('');
    try {
      if (type === 'refetch_company') {
        await apiFetch(`/companies/${encodeURIComponent(company)}/fetch`, { method: 'POST' });
      } else {
        await apiFetch('/companies', { method: 'POST', body: JSON.stringify({ name: company, aliases: [] }) });
        notifyCompanyAdded(company);
        setNotificationTick(t => t + 1);
        await fetchCompanies();
      }
      setChatAction(null);
      appendAssistantNote(
        chatId,
        `Collection started for ${company}. This takes a few minutes — I'll post the latest news here as soon as it's ready.`,
      );
      // `since` guards against an earlier sweep's finished_at being mistaken for
      // this one's - the status endpoint reports the last completed run.
      setWatchedFetch({ company, chatId, since: Date.now() });
    } catch (err) {
      setChatActionError(err.message || 'Could not start collection');
    } finally {
      setChatActionBusy(false);
    }
  }, [chatAction, fetchCompanies, appendAssistantNote]);

  // Polls the sweep the user just confirmed and announces it when it lands:
  // bell notification, popup, and the news answer posted into the same chat.
  useEffect(() => {
    if (!watchedFetch) return undefined;
    const { company, chatId, since } = watchedFetch;
    const giveUpAt = since + 12 * 60 * 1000;
    let cancelled = false;
    let timer = null;

    const finish = (result) => {
      if (cancelled) return;
      setWatchedFetch(null);
      notifyCompanyResultsReady(company, result.records, chatId);
      setNotificationTick(t => t + 1);
      setCompanyReady({ company, chatId, ...result });
      if (!result.timedOut) {
        fetchIntelligence();
        askInConversation(`What is the latest news about ${company}?`, chatId);
      }
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        const status = await apiFetch(`/companies/${encodeURIComponent(company)}/fetch-status`);
        const finishedAt = status.finished_at ? new Date(status.finished_at).getTime() : 0;
        if (status.status === 'idle' && finishedAt >= since) {
          finish({ records: status.records_added, failed: status.failed });
          return;
        }
      } catch {
        // transient - keep polling until the deadline
      }
      if (cancelled) return;
      if (Date.now() > giveUpAt) {
        finish({ records: 0, timedOut: true });
        return;
      }
      timer = setTimeout(tick, 10000);
    };

    timer = setTimeout(tick, 8000);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [watchedFetch, askInConversation, fetchIntelligence]);

  // Reuse an existing empty "New chat" when possible so refresh / double-clicks
  // don't keep stacking blank conversations next to real history.
  const startNewChat = useCallback(() => {
    setChatSessions(sessions => {
      const next = openOrCreateBlankSession(sessions, activeChatId);
      setActiveChatId(next.activeChatId);
      return next.sessions;
    });
  }, [activeChatId]);

  const switchChat = useCallback((chatId) => {
    setActiveChatId(chatId);
    navigateToView('ai', { keepChat: true });
  }, [navigateToView]);

  const deleteChat = useCallback((chatId) => {
    setChatSessions(sessions => {
      const remaining = sessions.filter(s => s.id !== chatId);
      if (remaining.length === 0) {
        const fresh = createChatSession();
        setActiveChatId(fresh.id);
        return [fresh];
      }
      if (chatId === activeChatId) setActiveChatId(remaining[0].id);
      return remaining;
    });
  }, [activeChatId]);

  // Same behavior as startNewChat — kept for AiPage prop compatibility.
  const resetConversation = startNewChat;

  // Deep-dive from the popup: navigate to the AI page and queue the question
  // for a useEffect to send once the page view is active (avoids calling
  // askInConversation before the AI page has actually mounted/switched to).
  const [pendingAiQuestion, setPendingAiQuestion] = useState(null);

  const goDeepDive = useCallback((question) => {
    const q = (question ?? popupQuestion).trim();
    setShowAiPopup(false);
    navigateToView('ai');
    if (q) setPendingAiQuestion(q);
  }, [popupQuestion, navigateToView]);

  useEffect(() => {
    if (view === 'ai' && pendingAiQuestion) {
      askInConversation(pendingAiQuestion);
      setPendingAiQuestion(null);
    }
  }, [view, pendingAiQuestion, askInConversation]);

  const summarizeRecord = useCallback(async (recordId) => {
    setSummarizingId(recordId);
    try {
      const updated = await apiFetch(`/intelligence/${encodeURIComponent(recordId)}/summarize`, { method: 'POST' });
      setRecords(rs => rs.map(r => (r.id === recordId ? { ...r, summary: updated.summary } : r)));
    } catch (err) {
      console.error('Summarize failed', err);
    } finally {
      setSummarizingId(null);
    }
  }, []);

  const toggleCompany = (name) => {
    setActiveCompanies(cs => cs.includes(name) ? cs.filter(c => c !== name) : [...cs, name]);
  };

  const bumpWatchlistRevision = useCallback(() => {
    setWatchlistRevision(v => v + 1);
  }, []);

  const toggleWatchlist = useCallback((recordId) => {
    setWatchlist(w => {
      const wasSaved = w.includes(recordId);
      const updated = wasSaved ? w.filter(id => id !== recordId) : [...w, recordId];

      if (wasSaved) {
        removeWatchlistSnapshot(recordId);
      } else {
        const record = records.find(r => recordKey(r) === recordId);
        if (record) saveWatchlistSnapshot(record);
      }

      localStorage.setItem('corpintel_watchlist', JSON.stringify(updated));
      return updated;
    });
    bumpWatchlistRevision();
  }, [records, bumpWatchlistRevision]);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRelatedRecords, setSelectedRelatedRecords] = useState([]);
  // Ordered list the story was opened from, so the detail page can step Previous / Next.
  const [selectedSequence, setSelectedSequence] = useState([]);

  const handleOpenRecord = useCallback((record, options = {}) => {
    if (!record) return;
    acknowledgeWatchlistItem(record);
    bumpWatchlistRevision();

    const primaryKey = recordKey(record);
    let related = options.relatedRecords || options.related || [];
    if (!Array.isArray(related)) related = [];

    // If caller didn't pass related articles, resolve same-story cluster from loaded records
    // so the detail popup can list every source when the list shows "2 sources".
    if (related.length === 0 && records.length > 0) {
      related = records.filter(r => {
        const key = recordKey(r);
        if (!key || key === primaryKey) return false;
        return areSameStory(record, r);
      });
    } else {
      related = related.filter(r => r && recordKey(r) !== primaryKey);
    }

    setSelectedRecord(record);
    setSelectedRelatedRecords(related);
    setSelectedSequence(Array.isArray(options.sequence) ? options.sequence : []);
    if (!record.summary && record.id) {
      summarizeRecord(record.id);
    }
  }, [bumpWatchlistRevision, records, summarizeRecord]);

  // Keep drawer content live when summarize/watchlist updates the records list
  const selectedRecordLive = useMemo(() => {
    if (!selectedRecord) return null;
    const key = recordKey(selectedRecord);
    return records.find(r => recordKey(r) === key) || selectedRecord;
  }, [selectedRecord, records]);

  const closeDetail = useCallback(() => {
    setSelectedRecord(null);
    setSelectedRelatedRecords([]);
    setSelectedSequence([]);
  }, []);

  // Switching sections (sidebar, notifications, Ask AI) leaves the story page.
  useEffect(() => { closeDetail(); }, [view, closeDetail]);

  const askAiAbout = useCallback((question) => {
    navigateToView('ai');
    if (question) setPendingAiQuestion(question);
  }, [navigateToView]);

  const selectedRelatedLive = useMemo(() => {
    if (!selectedRelatedRecords.length) return [];
    return selectedRelatedRecords.map(r => {
      const key = recordKey(r);
      return records.find(x => recordKey(x) === key) || r;
    });
  }, [selectedRelatedRecords, records]);

  useEffect(() => {
    watchlist.forEach(id => {
      const record = records.find(r => recordKey(r) === id);
      if (record) ensureWatchlistSnapshot(record);
    });
  }, [records, watchlist]);

  const selectCompany = useCallback((name) => {
    setActiveCompanies([name]);
    setView('intelligence');
  }, []);

  const handleEmailSent = useCallback((activity) => {
    setEmailActivity(prev => [activity, ...prev].slice(0, 50));
    notifyBriefSent(activity);
    setNotificationTick(t => t + 1);
  }, []);

  const openEmailCenter = useCallback(() => {
    setSettingsInitialMode('email');
    setView('settings');
  }, []);

  const handleAddCompany = useCallback(async ({ name, aliases = [] }) => {
    setAddingCompany(true);
    setAddCompanyError('');
    try {
      const created = await apiFetch('/companies', {
        method: 'POST',
        body: JSON.stringify({ name, aliases }),
      });
      setCompanies(prev => {
        const next = [...prev.filter(c => c.name !== created.name), { name: created.name, aliases: created.aliases || [] }];
        return next.sort((a, b) => a.name.localeCompare(b.name));
      });
      notifyCompanyAdded(created.name);
      setNotificationTick(t => t + 1);
      await fetchCompanies();
      await fetchIntelligence();
      return created;
    } catch (err) {
      setAddCompanyError(err.message || 'Failed to add company');
      return null;
    } finally {
      setAddingCompany(false);
    }
  }, [fetchIntelligence, fetchCompanies]);

  const openAddCompany = useCallback(() => {
    setAddCompanyError('');
    setShowAddCompany(true);
  }, []);

  // watchlistRevision is a manual bump counter (see bumpWatchlistRevision) for
  // localStorage-side watchlist mutations that don't change `watchlist`'s own
  // reference - it's an intentional recompute trigger, not dead weight.
  const watchlistUpdateCount = useMemo(
    () => countUpdatedWatchlistItems(records, watchlist),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, watchlist, watchlistRevision],
  );

  const updatedWatchlistIds = useMemo(
    () => getUpdatedWatchlistIds(records, watchlist),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, watchlist, watchlistRevision],
  );

  const alertCount = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return records.filter(r => r.published_at && new Date(r.published_at).getTime() >= weekAgo).length;
  }, [records]);

  const overviewCompanies = useMemo(() => {
    const byCompany = new Map();
    records.forEach(record => {
      if (record.entity_type === 'person') return;
      if (!record.company || !matchesDateFilter(record.published_at, dateFilter)) return;
      const current = byCompany.get(record.company);
      const publishedAt = record.published_at ? new Date(record.published_at).getTime() : 0;
      byCompany.set(record.company, {
        name: record.company,
        count: (current?.count || 0) + 1,
        latest: Math.max(current?.latest || 0, publishedAt),
      });
    });
    return [...byCompany.values()]
      .sort((a, b) => b.count - a.count || b.latest - a.latest || a.name.localeCompare(b.name))
      .map(({ name }) => ({ name }));
  }, [records, dateFilter]);

  // notificationTick is bumped after read/dismiss actions that mutate
  // notification storage without changing `records` itself - same intentional
  // recompute-trigger pattern as watchlistRevision above.
  const notifications = useMemo(
    () => buildNotificationFeed(records),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, notificationTick],
  );

  const notificationUnreadCount = useMemo(
    () => getUnreadNotificationCount(notifications),
    [notifications],
  );

  const handleNotificationAction = useCallback((item) => {
    markNotificationRead(item.id);
    setNotificationTick(t => t + 1);

    if (item.action === 'view_high_risk' || item.type === 'high_risk') {
      setSeverityFilter('high');
      setDateFilter({ preset: 'today', customDate: '' });
      setView('intelligence');
      return;
    }
    if (item.action === 'open_email' || item.type === 'brief_sent') {
      setSettingsInitialMode('email');
      setView('settings');
      return;
    }
    // A finished collection posted its answer into a specific chat - open that
    // chat instead of dumping the user on the Intelligence list.
    if (item.action === 'open_chat') {
      const chatId = item.meta?.chatId;
      if (chatId && chatSessions.some(s => s.id === chatId)) {
        switchChat(chatId);
        return;
      }
    }
    if (item.action === 'view_company' || item.action === 'open_chat' || item.type === 'company_added') {
      const company = item.meta?.company;
      if (company) setActiveCompanies([company]);
      setView('intelligence');
    }
  }, [chatSessions, switchChat]);

  const handleMarkAllNotificationsRead = useCallback(() => {
    markAllNotificationsRead(notifications.map(n => n.id));
    setNotificationTick(t => t + 1);
  }, [notifications]);

  // Analytics "By Category" bars navigate to Intelligence pre-filtered to that category.
  const navigateToIntelligenceByCategory = useCallback((category) => {
    setCategoryFilter(category);
    setView('intelligence');
  }, []);

  // ─── Chart Data ────────────────────────────────────────────────────────
  const categoryChartData = useMemo(() => {
    const counts = {};
    records.forEach(r => (r.categories || []).forEach(cat => counts[cat] = (counts[cat] || 0) + 1));
    return {
      labels: Object.keys(counts),
      datasets: [{
        label: 'Records by Category',
        data: Object.values(counts),
        backgroundColor: ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#6366f1'],
      }],
    };
  }, [records]);

  // ─── Main Layout with Sidebar ──────────────────────────────────────────
  const detailOpen = Boolean(selectedRecordLive);
  const aiLayout = view === 'ai' && !detailOpen;

  return (
    <div className={`flex font-sans antialiased bg-transparent ${aiLayout ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
      <Sidebar
        view={view}
        setView={navigateToView}
        intelligenceCount={totalRecords}
        companyCount={companies.length}
        watchlistUpdateCount={watchlistUpdateCount}
        alertCount={alertCount}
        companies={overviewCompanies}
        onSelectCompany={selectCompany}
        onAddCompany={openAddCompany}
      />

      {/* Main content */}
      <div className={`flex-1 min-w-0 ${aiLayout ? 'flex flex-col min-h-0 overflow-hidden' : ''}`}>
        <Header
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          openAiPopup={openAiPopup}
          notifications={notifications}
          notificationUnreadCount={notificationUnreadCount}
          onNotificationAction={handleNotificationAction}
          onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          view={view}
          emailActivity={emailActivity}
          emailPulseCount={emailPulseCount}
          onOpenEmailCenter={openEmailCenter}
        />

        <main className={
          aiLayout
            ? 'px-4 sm:px-6 lg:px-8 py-4 pb-20 md:pb-4 flex-1 min-h-0 flex flex-col overflow-hidden'
            : 'px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-6'
        }>
          <div className={
            view === 'dashboard' || detailOpen
              ? 'w-full max-w-[1400px] mx-auto'
              : view === 'ai'
                ? 'w-full max-w-[1200px] mx-auto flex-1 min-h-0 flex flex-col'
                : 'w-full max-w-6xl mx-auto'
          }>
            {detailOpen && (
              <AlertDetailPage
                backLabel={{ dashboard: 'Today', intelligence: 'All stories', companies: 'Companies', watchlist: 'Companies', briefs: 'Briefs', ai: 'AI Assistant', settings: 'Settings' }[view] || 'Back'}
                record={selectedRecordLive}
                related={selectedRelatedLive}
                sequence={selectedSequence}
                records={records}
                watchlist={watchlist}
                toggleWatchlist={toggleWatchlist}
                summarizingId={summarizingId}
                summarizeRecord={summarizeRecord}
                onBack={closeDetail}
                onNavigate={(rec) => handleOpenRecord(rec, { sequence: selectedSequence })}
                onAskAi={askAiAbout}
                onViewCompany={(name) => { closeDetail(); selectCompany(name); }}
                onEmailSent={handleEmailSent}
              />
            )}
            {/* Views stay mounted behind the story page so filters and scroll are kept on Back */}
            <div className={detailOpen ? 'hidden' : 'contents'}>
            {view === 'dashboard' && (
              <DashboardView
                records={records}
                loading={loading}
                companies={companies}
                watchlist={watchlist}
                onOpenRecord={handleOpenRecord}
                updatedWatchlistIds={updatedWatchlistIds}
                setView={setView}
                summarizingId={summarizingId}
                summarizeRecord={summarizeRecord}
                toggleWatchlist={toggleWatchlist}
                dateFilter={dateFilter}
                severityFilter={severityFilter}
                setSeverityFilter={setSeverityFilter}
                emailActivity={emailActivity}
                onEmailSent={handleEmailSent}
                onAddCompany={openAddCompany}
                onAskAi={askAiAbout}
                onViewCompany={selectCompany}
                onScheduleReport={() => {
                  setSettingsInitialMode('scheduler');
                  setView('settings');
                }}
              />
            )}
            {view === 'intelligence' && (
              <IntelligenceView
                records={records}
                loading={loading}
                error={error}
                totalRecords={totalRecords}
                companies={companies}
                activeCompanies={activeCompanies}
                toggleCompany={toggleCompany}
                categoryFilter={categoryFilter}
                setCategoryFilter={setCategoryFilter}
                severityFilter={severityFilter}
                setSeverityFilter={setSeverityFilter}
                summarizingId={summarizingId}
                summarizeRecord={summarizeRecord}
                watchlist={watchlist}
                toggleWatchlist={toggleWatchlist}
                updatedWatchlistIds={updatedWatchlistIds}
                onWatchlistAcknowledge={bumpWatchlistRevision}
                dateFilter={dateFilter}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                onEmailSent={handleEmailSent}
                onOpenRecord={handleOpenRecord}
              />
            )}
            {view === 'analytics' && (
              <AnalyticsView
                records={records}
                categoryChartData={categoryChartData}
                companyStats={companyStats}
                onCategoryClick={navigateToIntelligenceByCategory}
              />
            )}
            {view === 'runs' && <RunsView runLogs={runLogs} />}
            {(view === 'companies' || view === 'watchlist') && (
              <CompaniesView
                key={view}
                initialTab={view === 'watchlist' ? 'saved' : 'monitored'}
                companies={companies}
                records={records}
                watchlist={watchlist}
                toggleWatchlist={toggleWatchlist}
                updatedWatchlistIds={updatedWatchlistIds}
                setView={setView}
                onOpenRecord={handleOpenRecord}
                onAddCompany={openAddCompany}
                onViewStories={selectCompany}
                onCompaniesChange={fetchCompanies}
                onRefreshRecords={fetchIntelligence}
              />
            )}
            {view === 'ai' && (
              <AiPage
                conversation={conversation}
                aiPageInput={aiPageInput}
                setAiPageInput={setAiPageInput}
                askInConversation={askInConversation}
                resetConversation={resetConversation}
                aiModels={aiModels}
                selectedAiModel={selectedAiModel}
                onSelectAiModel={setSelectedAiModel}
                chatSessions={chatSessions}
                activeChatId={activeChatId}
                onSelectChat={switchChat}
                onDeleteChat={deleteChat}
                onNewChat={startNewChat}
                records={records}
              />
            )}
            {view === 'briefs' && (
              <BriefsView
                emailActivity={emailActivity}
                scheduledReports={scheduledReports}
                onReportsChange={setScheduledReports}
                onEmailSent={handleEmailSent}
                onOpenRecord={handleOpenRecord}
              />
            )}
            {view === 'settings' && (
              <SettingsView
                emailActivity={emailActivity}
                scheduledReports={scheduledReports}
                onReportsChange={setScheduledReports}
                onRefreshRecords={fetchIntelligence}
                initialMode={settingsInitialMode}
                onModeConsumed={() => setSettingsInitialMode(null)}
              />
            )}
            </div>
          </div>
        </main>
      </div>


      <MobileNav view={view} setView={navigateToView} alertCount={alertCount} />
      <BotIntroOverlay
        show={showBotIntro}
        onDone={handleBotIntroDone}
      />
      <ConfirmCompanyModal
        action={chatAction}
        busy={chatActionBusy}
        error={chatActionError}
        onConfirm={confirmChatAction}
        onCancel={() => { setChatAction(null); setChatActionError(''); }}
      />
      <CompanyReadyModal
        result={companyReady}
        onDismiss={() => setCompanyReady(null)}
        onView={() => {
          const { company, chatId } = companyReady || {};
          setCompanyReady(null);
          // The answer was posted into the chat that requested it.
          if (chatId && chatSessions.some(s => s.id === chatId)) {
            switchChat(chatId);
            return;
          }
          navigateToView('ai');
          if (company) setActiveCompanies([company]);
        }}
      />
      <AddCompanyModal
        open={showAddCompany}
        onClose={() => setShowAddCompany(false)}
        onSubmit={handleAddCompany}
        loading={addingCompany}
        error={addCompanyError}
      />
      <AiBotWidget
        open={showAiPopup}
        onOpen={openAiPopup}
        onClose={() => setShowAiPopup(false)}
        hidden={view === 'ai'}
        bubble={false}
        aiOverview={aiOverview}
        aiOverviewLoading={aiOverviewLoading}
        popupQuestion={popupQuestion}
        setPopupQuestion={setPopupQuestion}
        popupAnswer={popupAnswer}
        popupLoading={popupLoading}
        popupError={popupError}
        askInPopup={askInPopup}
        goDeepDive={goDeepDive}
      />
    </div>
  );
}

export default App;
